import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useCookies } from "react-cookie";
import ProductsAPIClient from "../../../api/clients/products";
import TitleArea from "../../Components/TitleArea";
import Loading from "../../Components/Loading";
import Notice from "../../Components/Notice";
import ModalPopup from "../../Components/ModalPopup";
import WYSIWYGContent from "../../Components/WYSIWYGContent";
import EmailAFriend from "../../Components/Forms/EmailAFriend";
import {
  arrEmpty,
  objEmpty,
  QUAM_QUOTE_COOKIE_KEY,
  useWindowSize,
} from "../../Helpers";
import { ContentContainer, ContentWrapper } from "../../../styles";
import { QuoteDescription } from "./styles";
import QuoteRow from "./QuoteRow";
import { initialFormData, formFields } from "./form.config";
import ContactFormAPIClient from "../../../api/clients/contactForm";

export default function QuoteTemplate({ title }) {
  const location = useLocation();
  const currentPath = location.pathname;

  const [cookies] = useCookies([QUAM_QUOTE_COOKIE_KEY]);
  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const windowSize = useWindowSize();

  const [formData, setFormData] = useState(initialFormData);

  const productIds = useMemo(() => {
    const prodIds = [];
    if (!objEmpty(cookies[QUAM_QUOTE_COOKIE_KEY])) {
      Object.entries(cookies[QUAM_QUOTE_COOKIE_KEY]).forEach((fav) =>
        prodIds.push(fav[0]),
      );
    }
    return prodIds;
  }, [cookies]);

  const productIdsString = JSON.stringify(productIds);

  useEffect(() => {
    const loadProducts = async () => {
      setIsLoading(true);
      const idsArray = JSON.parse(productIdsString);

      if (!arrEmpty(idsArray)) {
        try {
          const API = new ProductsAPIClient();
          const response = await API.getProductsById(idsArray);
          if (response.success) {
            setProducts(response.products);
          } else {
            setProducts(null);
          }
        } catch (err) {
          console.error(err);
        }
      } else {
        setProducts(null);
      }
      setIsLoading(false);
    };

    if (currentPath && currentPath !== "_error") {
      loadProducts();
    }
  }, [productIdsString, currentPath]);

  const handleEmailToFriend = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!emailModalOpen) setEmailModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.terms) {
      alert("You must agree to the Terms and Conditions to proceed.");
      return;
    }

    try {
      setIsLoading(true);

      const productsContent =
        products && products.length > 0
          ? products.map((p) => {
              const favData = cookies[QUAM_QUOTE_COOKIE_KEY]?.[p.id];
              const version = favData?.version
                ? ` (Version: ${favData.version})`
                : "";
              const quantity = favData?.quantity || 1;
              return `<tr><td style="text-transform: uppercase">${p.name || p.title}${version}</td><td>Qty: ${quantity}</td></tr>`;
            })
          : [];

      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      const currentUrl = window.location.href;

      const productsTable =
        productsContent.length > 0
          ? `<table border="1" cellpadding="5" style="border-collapse: collapse;">
               <tbody>${productsContent.join("")}</tbody>
             </table>`
          : "No products selected";

      const formattedFormData = {
        toClient: formData.businessEmail,
        subject: "Request a Quote of Favorites",
        bodyClient: `Hi,<br><br>We have received the following quote request of from you.<br><br>
        <b>URL:</b> ${currentUrl}<br>
        <b>Quote Name:</b> ${formData.name}<br>
        <b>Name:</b> ${fullName}<br>
        <b>Title:</b> ${formData.jobTitle}<br>
        <b>Company:</b> ${formData.businessName}<br>
        <b>Email:</b> ${formData.businessEmail}<br>
        <b>Phone:</b> ${formData.phone || "N/A"}<br>
        <b>Notes:</b> ${formData.notes || "None"}<br>
        <b>Products:</b><br>${productsTable}`,
        bodyAdmin: `Hi,<br><br>A quote request has been received.<br><br>
        <b>URL:</b> ${currentUrl}<br>
        <b>Quote Name:</b> ${formData.name}<br>
        <b>Name:</b> ${fullName}<br>
        <b>Title:</b> ${formData.jobTitle}<br>
        <b>Company:</b> ${formData.businessName}<br>
        <b>Email:</b> ${formData.businessEmail}<br>
        <b>Phone:</b> ${formData.phone || "N/A"}<br>
        <b>Notes:</b> ${formData.notes || "None"}<br>
        <b>Products:</b><br>${productsTable}`,
        url: currentUrl,
      };

      const API = new ContactFormAPIClient();
      const response = await API.submitContactForm(formattedFormData);

      if (response && response.success) {
        alert("Quote request submitted successfully!");
        setFormData(initialFormData);
      } else {
        alert("There was an issue submitting your request. Please try again.");
      }
    } catch (err) {
      console.error("Submission Error: ", err);
      alert("An unexpected error occurred. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to render fields dynamic to type/tags
  const renderField = (field) => {
    const FieldTag = field.tag;
    return (
      <fieldset key={field.name} className={field.wrapperClass}>
        <div className="flex justify-between">
          <label htmlFor={field.name} className="text-base">
            {field.label}
          </label>
          {field.hasExtraLabel && <span className="text-lg">. Required</span>}
        </div>
        <FieldTag
          type={field.tag === "input" ? field.type : undefined}
          name={field.name}
          id={field.name}
          className={field.className}
          value={formData[field.name]}
          onChange={handleChange}
          required={field.required}
        />
      </fieldset>
    );
  };

  return (
    <ContentContainer bgColor={`lightGray`}>
      <TitleArea title={title} area="pages" />
      <ContentWrapper currDevice={windowSize.currDevice} padding={`6em 0 0`}>
        {title?.description && (
          <QuoteDescription>
            <WYSIWYGContent content={title.description} slimline={true} />
          </QuoteDescription>
        )}
      </ContentWrapper>

      {isLoading ? (
        <Loading inclIcon={true} />
      ) : arrEmpty(products) ? (
        <Notice
          padding={`80px 6em 6em`}
          message={"You have no products selected at the moment."}
        />
      ) : (
        <ContentWrapper currDevice={windowSize.currDevice}>
          <form onSubmit={handleSubmit}>
            {renderField(formFields.find((f) => f.name === "name"))}

            {/* Products Section */}
            <div className="mb-8">
              {products.map((product) => (
                <QuoteRow key={product.id} product={product} />
              ))}
            </div>

            {renderField(formFields.find((f) => f.name === "notes"))}

            <hr className="mb-9 text-gray" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 lg:mb-12">
              {formFields
                .filter((f) => f.name !== "name" && f.name !== "notes")
                .map((field) => renderField(field))}
            </div>

            <div className="mb-24">
              <h4 className="text-lg capitalize">Newsletter Subscription</h4>
              <label className="text-xl">
                <input
                  type="checkbox"
                  name="newsletter"
                  className="mr-2"
                  checked={formData.newsletter}
                  onChange={handleChange}
                />
                I would like to be informed of the latest news, product info,
                and events.
              </label>

              <h4 className="text-lg capitalize mt-11">
                Terms and Conditions *
              </h4>
              <label className="text-xl">
                <input
                  type="checkbox"
                  name="terms"
                  className="mr-2"
                  checked={formData.terms}
                  onChange={handleChange}
                  required
                />
                By submitting this form, I agree with Quam&lsquo;s{" "}
                <Link
                  to="/page/terms-and-conditions"
                  className="text-jay-blue underline hover:no-underline"
                >
                  Terms and Conditions.
                </Link>
              </label>
            </div>

            <div>
              <div className="flex flex-col sm:flex-row gap-7 justify-center pb-8 lg:pb-28">
                <button
                  type="submit"
                  className="text-lg text-white cursor-pointer inline-flex items-center bg-error h-12 hover:bg-black px-8 py-7 justify-center min-w-52"
                >
                  Request a Quote
                </button>
                <button
                  onClick={(e) => handleEmailToFriend(e)}
                  className="text-lg text-black cursor-pointer inline-flex items-center bg-transparent border border-black hover:text-white h-12 hover:bg-black px-8 py-7 justify-center min-w-52"
                >
                  Share This List
                </button>
              </div>
            </div>
          </form>
        </ContentWrapper>
      )}
      <ModalPopup isOpen={emailModalOpen} handleClose={setEmailModalOpen}>
        <EmailAFriend title={"Email List"} content={products} />
      </ModalPopup>
    </ContentContainer>
  );
}
